import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WizardStepName } from '../../data/ppoints/constants';
import { WizardStepModel } from '../../features/wizard/models/WizardStepModel';
import { wizardSteps } from '../../features/wizard/models/WizardSteps';
import WizardStepService from '../../features/wizard/services/WizardStepService';
import { RootState } from '../store';

export interface WizardState {
    steps: WizardStepModel[] | null;
    fetching: boolean;
    error: boolean;
    initialized: boolean;
    selectedStepName: WizardStepName | null;
    hasUndoneSteps: boolean;
    skippedStepNames: WizardStepName[];
}

const initialState: WizardState = {
    steps: null,
    fetching: true,
    error: false,
    initialized: false,
    selectedStepName: null,
    hasUndoneSteps: false,
    skippedStepNames: [],
};

export const wizardThunk = createAsyncThunk<WizardStepModel[], void>('wizard/get', async (_, thunkAPI) => {
    try {
        return await WizardStepService.getStepsWithStatus();
    } catch (error) {
        // console.error(error);
        return thunkAPI.rejectWithValue(error);
    }
});

export const wizardMarkDoneThunk = createAsyncThunk<WizardStepName, { stepName: WizardStepName; skip?: boolean }>(
    'wizard/markdone',
    async ({ stepName }, thunkAPI) => {
        try {
            const state = thunkAPI.getState() as RootState;
            const ecomId = state.ecoms.selected?.id || null;
            if (!ecomId) {
                return thunkAPI.rejectWithValue('Ecom not selected');
            }
            await WizardStepService.markDone(stepName);
            return stepName;
        } catch (error) {
            // console.error(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

const wizardMarkDoneCases = (asyncThunk, builder: ActionReducerMapBuilder<WizardState>): void => {
    builder.addCase(asyncThunk.fulfilled, (state, action) => {
        if (state.steps) {
            state.steps = state.steps.map((step) => (step.name === action.payload ? { ...step, done: true } : step));
        }

        if (action.meta.arg?.skip && !state.skippedStepNames.includes(action.payload.response)) {
            state.skippedStepNames.push(action.payload.response);
        }

        const hasUndoneSteps = state.steps?.some(({ done }) => !done) || false;
        state.hasUndoneSteps = hasUndoneSteps;
        state.error = false;
    });
    builder.addCase(asyncThunk.pending, (state) => {
        state.error = false;
    });
    builder.addCase(asyncThunk.rejected, (state) => {
        state.error = true;
    });
};

const initSteps = (stepsWithStatus: WizardStepModel[]): WizardStepModel[] => {
    return wizardSteps.map((frontendStep) => {
        const stepWithStatus = stepsWithStatus.find((stepDB) => stepDB.name === frontendStep.name);
        return stepWithStatus ? { ...frontendStep, ...stepWithStatus } : { ...frontendStep, done: false };
    });
};

export const wizardSlice = createSlice({
    name: 'wizard',
    initialState,
    reducers: {
        setSelectedStepName: (state, action: PayloadAction<WizardStepName | null>) => {
            state.selectedStepName = action.payload;
        },
    },
    extraReducers: (builder) => {
        // wizardGetCases
        builder.addCase(wizardThunk.fulfilled, (state, action) => {
            state.steps = initSteps(action.payload);
            state.hasUndoneSteps = action.payload.some(({ done }) => !done);
            state.initialized = true;
            state.fetching = false;
            state.error = false;
        });
        builder.addCase(wizardThunk.pending, (state) => {
            state.initialized = false;
            state.fetching = true;
            state.error = false;
        });
        builder.addCase(wizardThunk.rejected, (state) => {
            state.initialized = false;
            state.fetching = false;
            state.error = true;
            // TODO: add banner!!!!
            // toastUtils.error('Failed to fetch data!');
        });

        wizardMarkDoneCases(wizardMarkDoneThunk, builder);
    },
});

export const wizardActions = wizardSlice.actions;

export default wizardSlice.reducer;
